import { useCallback, useMemo, useRef } from "react";
import { useDrag } from "react-dnd";
import moment from "moment-timezone";
import { Lock } from "lucide-react";
import BreakOvertimeIndicator from "../../../../../components/BreakOvertimeIndicator";
import SectionInfo from "../../../../../components/SectionInfo";
import Checkbox from "../../../../../../../../components/shared/inputs/Checkbox";
import ShiftTooltip from "../../../../../components/tooltip/ShiftTooltip";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useMenuContext } from "../../../../../hooks/context/useMenuContext";
import { useEventUser } from "../../../../../hooks/useEventUser";
import { useDefaultLevel } from "../../../../../hooks/useDefaultLevel";
import { useAddedPayCode } from "../../../../../../../TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { useIsTemplate } from "../../../../../hooks/useIsTemplate";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useShiftPreferenceStyle } from "../../../../../hooks/styles/useShiftPreferenceStyle";
import { getSectionDurations } from "../../../../../helpers/data/getSectionDurations";
import { renderSectionsDuration } from "../../../../../helpers/eventdisplay/renderSectionsDuration";
import OpenRequestIndicator from "../../../../../components/OpenRequestIndicator";
import { cn } from "../../../../../../../../lib/utils";

function Shift({ className, style, event, isOpen, setTooltipOpen }: any) {
    const localRef = useRef<HTMLDivElement>(null);
    const { openMenu } = useMenuContext();
    const { selectedEvents, ctrlShiftS, dispatch, type } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const user = useEventUser(event?.resourceId);
    const { canEdit, canCreate } = usePermissions();
    const defaultPayCode = useAddedPayCode(user);
    const checked = useMemo(() => {
        return !!selectedEvents.find((item: any) => item.id === event.id);
    }, [event.id, selectedEvents]);

    const canSelect = useMemo(() => {
        if (selectedEvents.length === 0) return true;

        const hasUnassignedSelected = selectedEvents.some(
            (item: any) => item.resourceId === "unassigned-shifts"
        );

        return hasUnassignedSelected
            ? event?.resourceId === "unassigned-shifts"
            : event?.resourceId !== "unassigned-shifts";
    }, [selectedEvents, event?.resourceId]);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: "EVENT",
        item: { ...event, user: event?.user?.id || event?.user },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        canDrag: (canCreate || canEdit) && !event?.locked
    }), [event, canCreate, canEdit]);

    const combinedRef = (node: HTMLDivElement | null) => {
        localRef.current = node;
        drag(node);
    };

    const handleDoubleClick = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch({
            type: "selected-cell-or-event",
            payload: {
                isCreating:
                    false,
                ...event,
                isTemplate,
                job: event?.sections?.[0]?.job,
                location: event?.sections?.[0]?.location,
                level1: event?.sections?.[0]?.level1,
                level2: event?.sections?.[0]?.level2,
                level3: event?.sections?.[0]?.level3,
                level4: event?.sections?.[0]?.level4,
                user
            }
        });
    }, [dispatch, event, user, isTemplate]);

    const onMouseLeave = useCallback((e: any) => {
        const copyActive = e.getModifierState("Control") || e.getModifierState("Meta");
        dispatch({ type: "drag-and-drop-copy", payload: copyActive })
    }, [dispatch]);

    const onEventChange = useCallback(() => {
        dispatch({ type: "select-event", payload: { event } })
    }, [dispatch, event]);

    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "my-profile") return;
        openMenu({
            id: "event-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: event,
            setTooltipOpen
        });
    }, [openMenu, type, event, setTooltipOpen]);

    const { overtimeDuration, breakDuration } = getSectionDurations(event?.sections);

    const defaultLevel = useDefaultLevel();

    const preferenceStyle = useShiftPreferenceStyle(event);

    return (
        <ShiftTooltip data={{ ...event, resource: user }} isOpen={isOpen}>
            <div
                ref={combinedRef}
                className={cn(
                    "work-block tw-bg-indigo-100 tw-rounded-md tw-px-3 tw-py-2 tw-cursor-pointer tw-border-l-2 tw-border-indigo-300",
                    "tw-flex align-center align-center tw-items-center tw-justify-center tw-absolute border-box tw-z-10",
                    event.status === "approved" && "tw-bg-green-100 tw-border-l-green-400",
                    event?.locked && "tw-bg-violet-100 tw-border-l-violet-400",
                    isTemplate && "tw-bg-indigo-100 tw-border-l-primary/30",
                    ctrlShiftS && "tw-pr-0",
                    className || "",
                )}
                style={{
                    ...style,
                    ...preferenceStyle,
                    opacity: isDragging ? 0.5 : 1,
                }}
                onDoubleClick={type !== "my-profile" ? handleDoubleClick : undefined}
                onMouseLeave={onMouseLeave}
                onContextMenu={onContextMenu}
            >
                <div className="tw-w-full">
                    <div className="tw-flex tw-flex-col">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <div className="tw-font-bold tw-text-xs tw-flex tw-items-center tw-gap-1">
                                {event?.locked && (<Lock size={12} strokeWidth={3} className="tw-mb-1" />)}
                                <OpenRequestIndicator event={event} />
                                {renderSectionsDuration(
                                    moment(event.endDate).diff(moment(event.startDate), "seconds"),
                                    event.sections
                                )}
                            </div>

                            <BreakOvertimeIndicator
                                breakDuration={breakDuration}
                                overtimeDuration={overtimeDuration}
                            />
                        </div>

                        <SectionInfo
                            section={event?.sections?.[0]}
                            payCode={defaultPayCode?.code}
                            defaultLevel={defaultLevel}
                            className="tw-pt-0.5"
                        />
                    </div>
                </div>

                {(ctrlShiftS && event?.type !== "open-shift" && type !== "my-profile") && (
                    <div
                        className="checkbox-container"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            id={event.id}
                            className="tw-bg-white"
                            checked={checked}
                            onCheckedChange={onEventChange}
                            disabled={!canSelect}
                        />
                    </div>
                )}
            </div>
        </ShiftTooltip>
    );
}

export default Shift;
